import {PrimeReactProvider} from "primereact/api";
import React from "react";
import ReactDOM from "react-dom/client";
import {Provider} from "react-redux";

import ServiceApi from "./api";
import AppContainer from "./app/AppContainer";
import {loadEnvironment} from "./environment";
import {useLanguage} from "./hooks/languageHooks";
import {initializeEnvironment} from "./redux/envSlice";
import {tryPerformInitialLogin} from "./redux/loginThunk";
import initStore from "./redux/store";
import {initializeTheme} from "./redux/themeThunk";

// Tailwind (replaces PrimeFlex, which is EOL). Bridge defines the --p-* tokens the
// tailwindcss-primeui plugin reads, mapped onto PrimeReact 10's lara variables; the
// entry pulls in the utilities. Preflight is disabled so it does not reset PrimeReact
// component styling. Loaded before App.scss so REVA's own rules still win on ties.
import "./style/primeui-bridge.css";
import "./style/tailwind.css";
// FormLayout subset (.field / .formgrid) still used by REVA, vendored from PrimeFlex.
import "./style/formlayout.scss";
// PrimeIcons
import "primeicons/primeicons.css";

// PrimeReact theme imported directly in index.xhtml, with theme switcher abilities:
// https://primereact.org/theming/#switchthemes
// OpenLayers
import "ol-layerswitcher/dist/ol-layerswitcher.css";
import "ol/ol.css";

// App Stylesheet
import "./style/App.scss";

// import i18n (needs to be bundled)
import {initializeI18n} from "./i18n";

const rootElement = document.getElementById("root");
if (!rootElement) {
    throw new Error("Could not find root element!");
}
const root = ReactDOM.createRoot(rootElement);

loadEnvironment()
    .then((env) => {
        // Initialize Service API
        const serviceApi = new ServiceApi(env.revaApiUrl, env.avmApiUrl);

        // Initialize Redux Store
        const store = initStore(env, serviceApi);

        // Initialize I18n
        initializeI18n(env);

        // Initialize Store
        store.dispatch(initializeEnvironment({appEnvironment: env}));
        store.dispatch(initializeTheme());

        // Try to auto-login (in the background), if a valid session token was stored
        void store.dispatch(tryPerformInitialLogin());

        root.render(
            <React.StrictMode>
                <Provider store={store}>
                    <InnerPrimeReactApp />
                </Provider>
            </React.StrictMode>,
        );
    })
    .catch((e) => console.error("App initialization failed", e));

/**
 * Inner application wrapper, which depends on the configured language, and sets this as default PrimeReact locale.
 * Otherwise, the initial locale from i18next will be overwritten by PrimeReact default language "en" during initialization,
 * which leeds to wrong default number formats or PrimeReact messages.
 * @constructor
 */
function InnerPrimeReactApp(): React.ReactNode {
    const language = useLanguage();
    return (
        <PrimeReactProvider value={{locale: language}}>
            <AppContainer />
        </PrimeReactProvider>
    );
}
